import { useState, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import brokerCompanyApi from '../../services/brokerCompanyApi';
import BrokerCompanyDetail from '../../components/broker/BrokerCompanyDetail';
import BrokerCompensationPanel from '../../components/broker/BrokerCompensationPanel';
import { useToast } from '../../components/shared/Toast';
import EmptyState from '../../components/shared/EmptyState';
import LoadingSkeleton from '../../components/shared/LoadingSkeleton';

const BrokerDetailPage = () => {
  const { id } = useParams();
  const { data: broker, isLoading: brokerLoading } = brokerCompanyApi.endpoints.getBrokerCompany.useQuery(id);
  const { data: offices = [], isLoading: officesLoading } = brokerCompanyApi.endpoints.getBrokerCompanyOffices.useQuery(id);
  const { data: users = [], isLoading: usersLoading } = brokerCompanyApi.endpoints.getBrokerCompanyUsers.useQuery(id);
  const [activateBroker] = brokerCompanyApi.endpoints.activateBrokerCompany.useMutation();
  const [disableBroker] = brokerCompanyApi.endpoints.disableBrokerCompany.useMutation();
  const [updateBroker] = brokerCompanyApi.endpoints.updateBrokerCompany.useMutation();
  const [inviteUser] = brokerCompanyApi.endpoints.inviteBrokerUser.useMutation();
  const [approveUser] = brokerCompanyApi.endpoints.approveUser.useMutation();
  const [editUser] = brokerCompanyApi.endpoints.editUser.useMutation();
  const [deleteUserMutation] = brokerCompanyApi.endpoints.deleteUser.useMutation();
  const [approveOffice] = brokerCompanyApi.endpoints.approveOffice.useMutation();
  const [editOfficeMutation] = brokerCompanyApi.endpoints.editOffice.useMutation();
  const [resendConfirmation] = brokerCompanyApi.endpoints.resendConfirmation.useMutation();
  const [preApproveBroker] = brokerCompanyApi.endpoints.preApproveBrokerCompany.useMutation();
  const { data: accountExecutives = [] } = brokerCompanyApi.endpoints.getAccountExecutives.useQuery();

  const { showToast, ToastContainer } = useToast();
  const [fileContactConflict, setFileContactConflict] = useState(null);
  const [pendingApproveForm, setPendingApproveForm] = useState(null);
  const loading = brokerLoading || officesLoading || usersLoading;

  const companies = useMemo(() => {
    if (!broker) return [];
    return [
      { id: broker.id, name: broker.name, isParent: true },
      ...offices.map((o) => ({ id: o.id, name: o.name, isParent: false })),
    ];
  }, [broker, offices]);

  if (loading) {
    return <LoadingSkeleton variant="detail" />;
  }

  if (!broker) {
    return (
      <EmptyState
        icon={<ErrorOutlineIcon />}
        title="Broker not found"
        action={<Button variant="contained" component={RouterLink} to="/lender/brokers">Back to Brokers</Button>}
      />
    );
  }

  const handleToggleStatus = async () => {
    if (broker.status === 'Active') {
      await disableBroker(id);
    } else {
      await activateBroker(id);
    }
  };

  const handleUpdateCompany = async (form) => {
    await updateBroker({ id, ...form }).unwrap();
  };

  const handleInviteUser = async (form) => {
    await inviteUser({ ...form, brokerCompanyId: id }).unwrap();
  };

  const handleApproveUser = async (form) => {
    try {
      await approveUser({ ...form, brokerCompanyId: id }).unwrap();
    } catch (err) {
      if (err?.status === 409 && err?.data?.existingFileContactId) {
        setPendingApproveForm(form);
        setFileContactConflict(err.data);
        return;
      }
      throw err;
    }
  };

  const handleEditUser = async (form) => {
    await editUser({ ...form, brokerCompanyId: id }).unwrap();
  };

  const handleDeleteUser = async (user) => {
    await deleteUserMutation({ userId: user.userId, brokerCompanyId: id }).unwrap();
  };

  const handleApproveOffice = async (form) => {
    await approveOffice({ ...form, brokerCompanyId: id }).unwrap();
  };

  const handleEditOffice = async (form) => {
    await editOfficeMutation({ ...form, brokerCompanyId: id }).unwrap();
  };

  const handlePreApprove = async () => {
    try {
      await preApproveBroker({ id }).unwrap();
      showToast(`${broker.name} has been pre-approved. An activation email has been sent to the broker admin.`);
    } catch (err) {
      if (err?.status === 409 && err?.data?.existingFileContactId) {
        setFileContactConflict(err.data);
      } else {
        showToast('Failed to pre-approve broker company.', 'danger');
      }
    }
  };

  const handleFileContactConflictResolve = async (conflictAction) => {
    const conflict = fileContactConflict;
    if (!conflict) return;

    try {
      const savedForm = pendingApproveForm;
      setFileContactConflict(null);
      setPendingApproveForm(null);

      if (savedForm) {
        // Retry user approval with conflict resolution fields
        await approveUser({
          ...savedForm,
          brokerCompanyId: id,
          fileContactConflictAction: conflictAction,
          existingFileContactId: conflict.existingFileContactId,
        }).unwrap();
        showToast('User has been approved.');
      } else {
        // Retry pre-approve with conflict resolution fields
        await preApproveBroker({
          id,
          conflictAction,
          existingFileContactId: conflict.existingFileContactId,
        }).unwrap();
        showToast(`${broker.name} has been pre-approved. An activation email has been sent to the broker admin.`);
      }
    } catch {
      showToast('Failed to resolve file contact conflict.', 'danger');
    }
  };

  const handleResendInvite = async (user) => {
    try {
      await resendConfirmation({ userId: user.userId }).unwrap();
      showToast(`Confirmation email resent to ${user.emailAddress}.`);
    } catch {
      showToast('Failed to resend confirmation email.', 'danger');
    }
  };

  return (
    <>
    {ToastContainer}
    <BrokerCompanyDetail
      broker={broker}
      offices={offices}
      users={users}
      mode="lender"
      onToggleStatus={handleToggleStatus}
      onPreApprove={handlePreApprove}
      onUpdateCompany={handleUpdateCompany}
      onInviteUser={handleInviteUser}
      onApproveUser={handleApproveUser}
      onEditUser={handleEditUser}
      onDeleteUser={handleDeleteUser}
      onApproveOffice={handleApproveOffice}
      onEditOffice={handleEditOffice}
      onResendInvite={handleResendInvite}
      canEditNmls={true}
      backLink={{ to: '/lender/brokers', label: 'Brokers' }}
      accountExecutives={accountExecutives}
      fileContactConflict={fileContactConflict}
      onFileContactConflictResolve={handleFileContactConflictResolve}
      onFileContactConflictDismiss={() => { setFileContactConflict(null); setPendingApproveForm(null); }}
    />
    <Box sx={{ mt: 4 }}>
      <BrokerCompensationPanel companies={companies} defaultCompanyId={id} />
    </Box>
    </>
  );
};

export default BrokerDetailPage;
